/**
 * ClientContactsDialog — Gestion des contacts multi-personas d'un client.
 * PM Spec 05/07/2026 : router les WhatsApp/Email au bon interlocuteur (CEO/Ops/Compta).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users, Plus, Trash2, Edit3, Star, Mail, Phone, Loader2, X,
  Package, Truck, Wallet, FileText, User as UserIcon,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import UserPicker from '../common/UserPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUCKETS = [
  { key: 'milestones', label: 'Jalons', icon: Package, color: 'emerald', desc: 'Op créée, livrée, complétée, annulée' },
  { key: 'logistics', label: 'Logistique', icon: Truck, color: 'blue', desc: 'Chargement, en route, chauffeur, véhicule' },
  { key: 'finance', label: 'Finance', icon: Wallet, color: 'amber', desc: 'Devis, factures, paiements' },
  { key: 'documents', label: 'Documents', icon: FileText, color: 'violet', desc: 'Bons signés, preuves, PDF' },
];

const ROLES = [
  { value: 'ceo', label: 'CEO / Décideur' },
  { value: 'ops', label: 'Ops / Magasinier' },
  { value: 'compta', label: 'Comptable / Finance' },
  { value: 'achats', label: 'Achats / Approvisionnement' },
  { value: 'autre', label: 'Autre' },
];

const emptyForm = () => ({
  name: '', role: 'autre', email: '', phone: '',
  linked_user_id: '', is_primary: false, notes: '',
  buckets: ['milestones', 'logistics', 'finance', 'documents'],
  channel_prefs: {
    milestones: { email: true, whatsapp: true },
    logistics: { email: true, whatsapp: true },
    finance: { email: true, whatsapp: true },
    documents: { email: true, whatsapp: true },
  },
});

const ClientContactsDialog = ({ open, onClose, client }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | 'new' | contact_id
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const clientId = client?.id;
  const clientLabel = client?.companyName || client?.company_name ||
    `${client?.firstName || client?.first_name || ''} ${client?.lastName || client?.last_name || ''}`.trim();

  const fetchContacts = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/clients/${clientId}/contacts`);
      setContacts(r.data.items || []);
    } catch {
      setContacts([]);
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { if (open) { fetchContacts(); setEditing(null); } }, [open, fetchContacts]);

  const startCreate = () => { setForm(emptyForm()); setEditing('new'); };

  const startEdit = (c) => {
    setForm({
      name: c.name || '',
      role: c.role || 'autre',
      email: c.email || '',
      phone: c.phone || '',
      linked_user_id: c.linked_user_id || '',
      is_primary: !!c.is_primary,
      notes: c.notes || '',
      buckets: c.buckets || [],
      channel_prefs: {
        milestones: c.channel_prefs?.milestones || { email: false, whatsapp: false },
        logistics: c.channel_prefs?.logistics || { email: false, whatsapp: false },
        finance: c.channel_prefs?.finance || { email: false, whatsapp: false },
        documents: c.channel_prefs?.documents || { email: false, whatsapp: false },
      },
    });
    setEditing(c.id);
  };

  const toggleBucket = (bucketKey) => {
    setForm((f) => {
      const has = f.buckets.includes(bucketKey);
      return {
        ...f,
        buckets: has ? f.buckets.filter((b) => b !== bucketKey) : [...f.buckets, bucketKey],
        channel_prefs: {
          ...f.channel_prefs,
          [bucketKey]: has
            ? { email: false, whatsapp: false }
            : { email: true, whatsapp: true },
        },
      };
    });
  };

  const toggleChannel = (bucketKey, channel) => {
    setForm((f) => ({
      ...f,
      channel_prefs: {
        ...f.channel_prefs,
        [bucketKey]: {
          ...f.channel_prefs[bucketKey],
          [channel]: !f.channel_prefs[bucketKey]?.[channel],
        },
      },
    }));
  };

  const submit = async () => {
    if (!form.name.trim()) { toast({ title: 'Nom requis', variant: 'destructive' }); return; }
    if (!form.email && !form.phone) {
      toast({ title: 'Email ou téléphone requis', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.linked_user_id) delete payload.linked_user_id;
      if (editing === 'new') {
        await axios.post(`${API}/clients/${clientId}/contacts`, payload);
        toast({ title: 'Contact créé ✅' });
      } else {
        await axios.patch(`${API}/clients/${clientId}/contacts/${editing}`, payload);
        toast({ title: 'Contact mis à jour ✅' });
      }
      setEditing(null);
      fetchContacts();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const deleteContact = async (c) => {
    if (!window.confirm(`Supprimer le contact "${c.name}" ?`)) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/contacts/${c.id}`);
      toast({ title: 'Contact supprimé' });
      fetchContacts();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail, variant: 'destructive' });
    }
  };

  const setPrimary = async (c) => {
    try {
      await axios.post(`${API}/clients/${clientId}/contacts/${c.id}/set-primary`, {});
      toast({ title: `${c.name} défini comme contact principal` });
      fetchContacts();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail, variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="client-contacts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Contacts & Notifications — {clientLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
          💡 Chaque contact reçoit uniquement les notifications des <strong>buckets</strong> qui lui sont attribués,
          sur les <strong>canaux</strong> activés. Le <strong>contact principal</strong> (⭐) reçoit tout ce qui n&apos;est
          attribué à personne (fallback).
        </div>

        {!editing && (
          <div className="mt-4 space-y-3" data-testid="contacts-list">
            {loading ? (
              <div className="h-32 flex items-center justify-center text-zinc-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 border-2 border-dashed rounded-lg">
                Aucun contact configuré.
              </div>
            ) : (
              contacts.map((c) => (
                <div key={c.id} className="bg-white border border-zinc-200 rounded-lg p-4"
                  data-testid={`contact-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {c.is_primary && (
                          <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300">
                            <Star className="h-3 w-3 mr-0.5" /> Principal
                          </Badge>
                        )}
                        <span className="font-semibold text-zinc-950">{c.name}</span>
                        <Badge variant="outline" className="text-[10px] uppercase">
                          {ROLES.find((r) => r.value === c.role)?.label || c.role}
                        </Badge>
                        {c.linked_user_id && (
                          <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                            <UserIcon className="h-3 w-3 mr-0.5" /> User lié
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
                        {c.email && <span><Mail className="inline h-3 w-3 mr-0.5" /> {c.email}</span>}
                        {c.phone && <span><Phone className="inline h-3 w-3 mr-0.5" /> {c.phone}</span>}
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        {BUCKETS.map((b) => {
                          const has = c.buckets?.includes(b.key);
                          const prefs = c.channel_prefs?.[b.key] || {};
                          if (!has) return null;
                          const Icon = b.icon;
                          return (
                            <span key={b.key}
                              className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-${b.color}-50 text-${b.color}-700 border border-${b.color}-200`}>
                              <Icon className="h-3 w-3" />
                              {b.label}
                              {prefs.email && <Mail className="h-2.5 w-2.5 opacity-70" />}
                              {prefs.whatsapp && <Phone className="h-2.5 w-2.5 opacity-70" />}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!c.is_primary && (
                        <button onClick={() => setPrimary(c)}
                          className="p-1.5 rounded hover:bg-amber-50 text-zinc-400 hover:text-amber-600"
                          title="Définir comme principal"
                          data-testid={`contact-set-primary-${c.id}`}>
                          <Star className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => startEdit(c)}
                        className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
                        title="Modifier" data-testid={`contact-edit-${c.id}`}>
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button onClick={() => deleteContact(c)}
                        className="p-1.5 rounded hover:bg-rose-50 text-zinc-400 hover:text-rose-600"
                        title="Supprimer" data-testid={`contact-delete-${c.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}

            <div className="pt-2">
              <Button onClick={startCreate} className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="contact-add-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Ajouter un contact
              </Button>
            </div>
          </div>
        )}

        {editing && (
          <div className="mt-4 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Nom *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex : M. Kouassi" data-testid="contact-name-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Rôle</Label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full h-10 rounded-md border border-zinc-200 text-sm px-2 mt-1"
                  data-testid="contact-role-select">
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="prenom.nom@societe.ci" data-testid="contact-email-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Téléphone (WhatsApp)</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+225 XX XX XX XX XX" data-testid="contact-phone-input" />
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                Lier à un utilisateur existant (optionnel)
              </Label>
              <UserPicker
                value={form.linked_user_id}
                onChange={(user) => {
                  if (!user) {
                    setForm({ ...form, linked_user_id: '' });
                    return;
                  }
                  // Auto-remplit email et phone si vides
                  setForm(prev => ({
                    ...prev,
                    linked_user_id: user.id,
                    email: prev.email || user.email || '',
                    phone: prev.phone || user.phone || '',
                    name: prev.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                  }));
                }}
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_primary}
                onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
                data-testid="contact-primary-check" />
              <Star className="h-4 w-4 text-amber-500" />
              <span className="text-sm">Contact <strong>principal</strong> (fallback universel)</span>
            </label>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">
                Notifications reçues (buckets × canaux)
              </Label>
              <div className="space-y-2">
                {BUCKETS.map((b) => {
                  const Icon = b.icon;
                  const active = form.buckets.includes(b.key);
                  return (
                    <div key={b.key} className={`border rounded-lg p-3 transition ${active ? `border-${b.color}-300 bg-${b.color}-50/30` : 'border-zinc-200 bg-white'}`}
                      data-testid={`bucket-${b.key}`}>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 cursor-pointer flex-1">
                          <input type="checkbox" checked={active}
                            onChange={() => toggleBucket(b.key)}
                            data-testid={`bucket-check-${b.key}`} />
                          <Icon className={`h-4 w-4 text-${b.color}-600`} />
                          <span className="font-semibold text-sm">{b.label}</span>
                          <span className="text-xs text-zinc-500">— {b.desc}</span>
                        </label>
                        {active && (
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1 cursor-pointer text-xs">
                              <input type="checkbox"
                                checked={!!form.channel_prefs[b.key]?.email}
                                onChange={() => toggleChannel(b.key, 'email')}
                                data-testid={`bucket-${b.key}-email`} />
                              <Mail className="h-3 w-3" /> Email
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer text-xs">
                              <input type="checkbox"
                                checked={!!form.channel_prefs[b.key]?.whatsapp}
                                onChange={() => toggleChannel(b.key, 'whatsapp')}
                                data-testid={`bucket-${b.key}-whatsapp`} />
                              <Phone className="h-3 w-3" /> WhatsApp
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between pt-3 border-t">
              <Button variant="ghost" onClick={() => setEditing(null)}>
                <X className="h-4 w-4 mr-1" /> Annuler
              </Button>
              <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="contact-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                {editing === 'new' ? 'Créer le contact' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientContactsDialog;
